<template>
  <div
    ref="rootRef"
    class="region-picker"
    :class="[variantClass, { 'is-open': open }]"
  >
    <button
      type="button"
      class="region-picker__trigger"
      aria-haspopup="listbox"
      :aria-expanded="open"
      @click="toggle"
    >
      <span>{{ label }}</span>
      <span class="region-picker__caret" aria-hidden="true" />
    </button>
    <div v-if="open" class="region-picker__panel" role="listbox">
      <div class="region-picker__body">
        <div class="region-picker__col region-picker__col--area">
          <button
            type="button"
            class="region-picker__item"
            :class="{ 'is-selected': draftAreaId == null && draftPrefectureId == null }"
            @click="pickNationwide"
          >
            全国
          </button>
          <button
            v-for="area in areaOptions"
            :key="`area-${area.id}`"
            type="button"
            class="region-picker__item"
            :class="{
              'is-active': focusedAreaId === area.id,
              'is-selected': draftAreaId === area.id,
            }"
            @click="pickArea(area)"
          >
            {{ area.name }}
          </button>
        </div>
        <div v-if="focusedAreaId != null" class="region-picker__col region-picker__col--pref">
          <div v-if="prefectureLoading" class="region-picker__hint">読込中…</div>
          <button
            v-for="pref in prefectureOptions"
            :key="`pref-${pref.id}`"
            type="button"
            class="region-picker__item"
            :class="{ 'is-selected': draftPrefectureId === pref.id }"
            @click="pickPrefecture(pref)"
          >
            {{ pref.name }}
          </button>
        </div>
      </div>
      <div class="region-picker__footer">
        <span class="region-picker__draft">{{ draftLabel }}</span>
        <button type="button" class="region-picker__confirm" @click="confirm">
          確定
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { getAreaListService, getPrefectureListService } from '@/api/dp/index.js'

const props = defineProps({
  /** 適用済みの表示ラベル（trigger に表示） */
  label: {
    type: String,
    default: '全国',
  },
  /** 適用済み選択：パネル表示時にドラフトへ同期する */
  areaId: {
    type: Number,
    default: null,
  },
  prefectureId: {
    type: Number,
    default: null,
  },
  /** 'header' = PC ヘッダー / 'mp' = スマホ mp-summary */
  variant: {
    type: String,
    default: 'header',
  },
})
const emit = defineEmits(['confirm', 'open-change'])

const rootRef = ref(null)
const open = ref(false)
const areaOptions = ref([])
const focusedAreaId = ref(null)
const prefectureOptions = ref([])
const prefectureLoading = ref(false)
const prefectureCache = new Map()
/** 面板内草稿：点「確定」后才生效 */
const draftAreaId = ref(null)
const draftPrefectureId = ref(null)

const variantClass = computed(() =>
  props.variant === 'mp' ? 'region-picker--mp' : 'region-picker--header',
)
const draftLabel = computed(() => buildLabel(draftAreaId.value, draftPrefectureId.value))

function buildLabel(areaId, prefectureId) {
  if (areaId == null && prefectureId == null) return '全国'
  const area = areaOptions.value.find((item) => item.id === areaId)
  if (prefectureId != null) {
    const pref = prefectureOptions.value.find((item) => item.id === prefectureId)
      || (prefectureCache.get(areaId) || []).find((item) => item.id === prefectureId)
    if (area && pref) return `${area.name}・${pref.name}`
    if (pref) return pref.name
  }
  return area?.name ?? '全国'
}

function syncDraftFromSelection() {
  draftAreaId.value = props.areaId
  draftPrefectureId.value = props.prefectureId
  focusedAreaId.value = props.areaId
  if (props.areaId != null) {
    void loadPrefecturesForArea(props.areaId)
  } else {
    prefectureOptions.value = []
  }
}

function setOpen(value) {
  open.value = value
  emit('open-change', value)
  if (value) syncDraftFromSelection()
}

function toggle() {
  setOpen(!open.value)
}

function close() {
  if (open.value) setOpen(false)
}

function onPointerDownOutside(event) {
  if (!open.value) return
  const root = rootRef.value
  if (root && !root.contains(event.target)) {
    close()
  }
}

async function loadAreaOptions() {
  try {
    const res = await getAreaListService()
    areaOptions.value = (res?.rows || []).map((item) => ({
      id: item.id,
      name: item.name,
    }))
  } catch (error) {
    console.error('[dashboard] getAreaListService failed', error)
    areaOptions.value = []
  }
}

async function loadPrefecturesForArea(areaId) {
  if (areaId == null) {
    prefectureOptions.value = []
    return
  }
  if (prefectureCache.has(areaId)) {
    prefectureOptions.value = prefectureCache.get(areaId)
    return
  }
  prefectureLoading.value = true
  try {
    const res = await getPrefectureListService({ areaId })
    const rows = (res?.rows || []).map((item) => ({
      id: item.id,
      name: item.name,
    }))
    prefectureCache.set(areaId, rows)
    if (focusedAreaId.value === areaId) {
      prefectureOptions.value = rows
    }
  } catch (error) {
    console.error('[dashboard] getPrefectureListService failed', error)
    if (focusedAreaId.value === areaId) {
      prefectureOptions.value = []
    }
  } finally {
    prefectureLoading.value = false
  }
}

function pickNationwide() {
  focusedAreaId.value = null
  prefectureOptions.value = []
  draftAreaId.value = null
  draftPrefectureId.value = null
}

function pickArea(area) {
  focusedAreaId.value = area.id
  draftAreaId.value = area.id
  draftPrefectureId.value = null
  prefectureOptions.value = []
  void loadPrefecturesForArea(area.id)
}

function pickPrefecture(pref) {
  draftAreaId.value = focusedAreaId.value
  draftPrefectureId.value = pref.id
}

function confirm() {
  emit('confirm', {
    areaId: draftAreaId.value,
    prefectureId: draftPrefectureId.value,
    label: buildLabel(draftAreaId.value, draftPrefectureId.value),
  })
  setOpen(false)
}

// 親（PC 側など別インスタンス）で選択が変わった場合、開いていればドラフトを追従
watch(
  () => [props.areaId, props.prefectureId],
  () => {
    if (open.value) syncDraftFromSelection()
  },
)

onMounted(() => {
  document.addEventListener('pointerdown', onPointerDownOutside, true)
  void loadAreaOptions()
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onPointerDownOutside, true)
})

defineExpose({ close })
</script>

<style lang="less" scoped>
/* 悬浮细滚动条：透明轨道 + padding-box 缩进，桌面悬停显色 */
.mc-scrollbar() {
  scrollbar-width: thin;
  scrollbar-color: var(--mc-scrollbar-thumb) transparent;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 6px 0;
  }

  &::-webkit-scrollbar-thumb {
    min-height: 40px;
    background-color: var(--mc-scrollbar-thumb);
    background-clip: padding-box;
    border: 3px solid transparent;
    border-radius: 999px;
    transition: background-color 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: var(--mc-scrollbar-thumb-hover);
  }

  &::-webkit-scrollbar-thumb:active {
    background-color: var(--mc-scrollbar-thumb-active);
  }
}

.region-picker--header .region-picker__trigger {
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  font-size: 13px;
}

.region-picker {
  position: relative;
  display: inline-flex;
  z-index: 12;
}

.region-picker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 160px;
  padding: 4px 10px;
  border: 1px solid var(--mc-border);
  border-radius: 14px;
  background: color-mix(in srgb, var(--mc-accent) 18%, var(--mc-bg));
  color: var(--mc-text);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  > span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.region-picker__caret {
  flex: none;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  opacity: 0.7;
  transition: transform 0.15s ease;
}

.region-picker.is-open .region-picker__caret {
  transform: rotate(180deg);
}

.region-picker__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 240px;
  max-height: min(400px, 60vh);
  overflow: hidden;
  border: 1px solid var(--mc-border);
  border-radius: 10px;
  background: var(--mc-bg);
  box-shadow: var(--shadow-sm, 0 8px 24px rgba(0, 0, 0, 0.12));
  /* 基准行高：隔断从 .dashboard-header(line-height:56px) 继承来的行高，
     使面板内所有文字（列表项/提示/草稿/确定）在生产环境下保持正常高度 */
  line-height: 1.4;
  transform: translateX(-50%);
}

.region-picker__body {
  display: flex;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.region-picker__col {
  display: flex;
  flex-direction: column;
  min-width: 108px;
  max-height: min(300px, 48vh);
  overflow-y: auto;
  .mc-scrollbar();
}

.region-picker__col--area {
  border-right: 1px solid var(--mc-border);
  background: var(--mc-bg-soft);
}

.region-picker__col--pref {
  min-width: 120px;
}

.region-picker__item {
  display: block;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  background: transparent;
  color: var(--mc-text);
  font-size: 12px;
  font-weight: 500;
  /* 显式设定行高：项目为 <button>，需隔断从 .dashboard-header(line-height:56px)
     继承来的行高（生产环境 element-plus reset 使 button 继承行高，导致项目过高） */
  line-height: 1.4;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--mc-row-hover);
  }

  &.is-active {
    background: var(--mc-row-hover);
    font-weight: 700;
  }

  &.is-selected {
    background: var(--mc-row-selected);
    font-weight: 700;
  }
}

.region-picker__hint {
  padding: 10px 12px;
  color: var(--mc-text-muted);
  font-size: 12px;
}

.region-picker__footer {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-top: 1px solid var(--mc-border);
  background: var(--mc-bg);
}

.region-picker__draft {
  min-width: 0;
  overflow: hidden;
  color: var(--mc-text-muted);
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-picker__confirm {
  flex: none;
  min-width: 64px;
  padding: 6px 14px;
  border: 0;
  border-radius: 8px;
  background: var(--brand-color, #f9da49);
  color: #fff; // 浅色红底用白字保证对比；深色黄底见下方 html.dark 覆盖
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 1px 3px color-mix(in srgb, #000 12%, transparent);

  &:hover {
    filter: brightness(0.97);
  }

  &:active {
    transform: translateY(1px);
  }
}

.region-picker--mp {
  flex: none;
  z-index: 13;

  .region-picker__trigger {
    padding: 2px 8px;
    border: 0;
    border-radius: 12px;
    background: color-mix(in srgb, var(--mc-accent) 22%, var(--mc-bg));
  }

  .region-picker__panel {
    left: 0;
    transform: none;
  }
}
</style>
